import React from 'react';
import { WarningFilled } from '@ant-design/icons';
import styles from './styles/sso-identity-bar.module.scss';

/**
 * The persistent "you are impersonating someone" strip, rendered by the HOST
 * around the embedded consumer app — never inside it.
 *
 * That placement is the whole point. This bar sits outside the React Native
 * tree, so nothing the consumer app does can take it away: not rn-ui navigating
 * between screens, not a full-screen modal it renders, and not the member's own
 * session expiring inside the app. Whatever state the embed ends up in, the
 * admin can still see whose account they are in and still has a way out.
 *
 * Non-dismissible on purpose: there is no close affordance, only Exit, which
 * ends the session rather than hiding the reminder of it.
 *
 * Styled as an informational banner, not an error — see the stylesheet.
 *
 * @param {Object} props
 * @param {{givenName?: string, familyName?: string, email?: string}} [props.identity]
 *   From the SSO session bundle.
 * @param {string} [props.fallbackName] Used when the session returned no name.
 * @param {boolean} [props.exiting] True while the exit is in flight.
 * @param {() => void} props.onExit
 */
function SsoIdentityBar({ identity, fallbackName, exiting, onExit }) {
    const fullName =
        [identity?.givenName, identity?.familyName].filter(Boolean).join(' ') || fallbackName || 'this user';

    return (
        <div className={styles.bar} role="status">
            <span className={styles.icon} aria-hidden="true">
                <WarningFilled />
            </span>
            <div className={styles.identity}>
                <span className={styles.statement}>You are inside {fullName}&apos;s account</span>
                {identity?.email ? <span className={styles.email}>{identity.email}</span> : null}
            </div>
            <button type="button" className={styles.exitBtn} onClick={onExit} disabled={!!exiting}>
                {exiting ? 'Exiting…' : 'Exit'}
            </button>
        </div>
    );
}

export default SsoIdentityBar;
